/**
 * Admin → Quick Reason Chips (QC-CHIPS-UI-001 premium redesign).
 * Frontend only — uses the existing /api/admin/quick-chips endpoints unchanged.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { TooltipProvider } from '../components/ui/tooltip';
import { Search, X, Filter, ChevronDown, Plus, ArrowUpDown, Check, Lightbulb, CheckCircle2, AlertTriangle } from 'lucide-react';
import { TABS, TAB_COLORS, isMlsCategory } from '../components/admin/quickchips/chipsConfig';
import { QuickChipsHeader } from '../components/admin/quickchips/QuickChipsHeader';
import { CategoryTabs } from '../components/admin/quickchips/CategoryTabs';
import { ChipTable } from '../components/admin/quickchips/ChipTable';
import { ChipEditorDialog } from '../components/admin/quickchips/ChipEditorDialog';
import '../components/admin/quickchips/quick-chips-premium.css';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

const AdminQuickChipsSettings = () => {
  const [chips, setChips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reorderMode, setReorderMode] = useState(false);
  const [editor, setEditor] = useState(null); // { mode, chip, category }
  const [chipPendingDelete, setChipPendingDelete] = useState(null);
  const [savedAt, setSavedAt] = useState(0);

  const token = localStorage.getItem('token');
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const fetchChips = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/quick-chips`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch');
      setChips(await res.json());
    } catch { toast.error('Failed to load chips'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchChips(); }, [fetchChips]);
  useEffect(() => { setEditor(null); setReorderMode(false); }, [activeTab]);
  useEffect(() => {
    if (!savedAt) return undefined;
    const t = setTimeout(() => setSavedAt(0), 2800);
    return () => clearTimeout(t);
  }, [savedAt]);
  const flashSaved = () => setSavedAt(Date.now());

  const categoryChips = useMemo(() => chips.filter(c => c.category === activeTab).sort(byOrder), [chips, activeTab]);
  const rows = useMemo(() => {
    if (reorderMode) return categoryChips;
    let list = categoryChips;
    if (statusFilter === 'active') list = list.filter(c => c.is_active);
    if (statusFilter === 'inactive') list = list.filter(c => !c.is_active);
    const q = searchQuery.trim().toLowerCase();
    if (q) list = list.filter(c => c.label.toLowerCase().includes(q) || (c.template || '').toLowerCase().includes(q));
    return list;
  }, [categoryChips, statusFilter, searchQuery, reorderMode]);

  const counts = useMemo(() => Object.fromEntries(TABS.map(t => [t.key, chips.filter(c => c.category === t.key && c.is_active).length])), [chips]);
  const activeCount = counts[activeTab] || 0;
  const totalCount = categoryChips.length;
  const tabDef = TABS.find(t => t.key === activeTab);
  const colors = TAB_COLORS[activeTab];

  /* ── API handlers (endpoints unchanged) ── */
  const request = async (path, method, body) => {
    const res = await fetch(`${API}${path}`, { method, headers: authHeaders, body: body ? JSON.stringify(body) : undefined });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
    return res.json();
  };

  const handleSave = async (data) => {
    try {
      if (editor.mode === 'edit') {
        const updated = await request(`/admin/quick-chips/${editor.chip.id}`, 'PUT', data);
        setChips(prev => prev.map(c => (c.id === updated.id ? updated : c)));
        toast.success('Chip updated');
      } else {
        const created = await request('/admin/quick-chips', 'POST', { ...data, category: activeTab });
        setChips(prev => [...prev, created]);
        toast.success(editor.mode === 'duplicate' ? 'Chip duplicated' : 'Chip created');
      }
      flashSaved();
      return true;
    } catch (e) { toast.error(e.message); return false; }
  };

  const handleToggle = async (chip, isActive) => {
    setChips(prev => prev.map(c => (c.id === chip.id ? { ...c, is_active: isActive } : c)));
    try {
      const updated = await request(`/admin/quick-chips/${chip.id}`, 'PUT', { is_active: isActive });
      setChips(prev => prev.map(c => (c.id === updated.id ? updated : c)));
      toast.success(isActive ? `“${chip.label}” activated` : `“${chip.label}” deactivated`);
      flashSaved();
    } catch (e) {
      setChips(prev => prev.map(c => (c.id === chip.id ? { ...c, is_active: !isActive } : c)));
      toast.error(e.message);
    }
  };

  const confirmDelete = async () => {
    if (!chipPendingDelete) return;
    const chip = chipPendingDelete;
    setChipPendingDelete(null);
    try {
      await request(`/admin/quick-chips/${chip.id}`, 'DELETE');
      setChips(prev => prev.filter(c => c.id !== chip.id));
      toast.success('Chip deleted permanently');
      flashSaved();
    } catch (e) { toast.error(e.message); }
  };

  const handleReorder = async (orderedIds) => {
    const orderMap = Object.fromEntries(orderedIds.map((id, i) => [id, i + 1]));
    setChips(prev => prev.map(c => (orderMap[c.id] ? { ...c, order: orderMap[c.id] } : c)));
    try {
      await request('/admin/quick-chips/reorder', 'POST', { category: activeTab, chip_ids: orderedIds });
      flashSaved();
    } catch (e) { toast.error(e.message); fetchChips(); }
  };

  const openCreate = () => setEditor({ mode: 'create', chip: null, category: activeTab });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="quick-chips-loading">
        <div className="w-8 h-8 border-[3px] border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="qc-page" data-testid="quick-chips-admin" style={{ '--qc-accent': colors.accent, '--qc-soft': colors.soft, '--qc-tile': colors.tile, '--qc-text': colors.text }}>
        <QuickChipsHeader />
        <CategoryTabs activeTab={activeTab} counts={counts} onChange={setActiveTab} />

        <section className="qc-panel" data-testid="chips-panel">
          {reorderMode ? (
            <div className="qc-reorder-bar" data-testid="reorder-bar">
              <span className="qc-reorder-title"><ArrowUpDown className="w-4 h-4" />Reorder mode · {tabDef.label}</span>
              <span className="qc-reorder-hint">Drag rows to arrange. Each drop is saved immediately and applies to every processor.</span>
              <button type="button" className="qc-btn qc-btn-primary ml-auto" style={{ '--qc-accent': '#4f46e5' }} onClick={() => setReorderMode(false)} data-testid="reorder-done-btn">
                <Check className="w-4 h-4" />Done
              </button>
            </div>
          ) : (
            <div className="qc-toolbar" data-testid="chips-toolbar">
              <div className="qc-search">
                <Search className="qc-search-icon w-4 h-4" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search chips…" data-testid="chip-search-input" />
                {searchQuery && (
                  <button type="button" className="qc-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search" data-testid="chip-search-clear"><X className="w-3.5 h-3.5" /></button>
                )}
              </div>
              <label className="qc-select">
                <Filter className="w-4 h-4 text-slate-500" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Filter by status" data-testid="chip-status-filter">
                  <option value="all">All Status</option>
                  <option value="active">Active only</option>
                  <option value="inactive">Inactive only</option>
                </select>
                <ChevronDown className="qc-select-caret w-4 h-4" />
              </label>
              <span className="qc-meta" data-testid="chips-active-count">{activeCount} of {totalCount} active</span>
              <button type="button" className="qc-btn qc-btn-ghost" onClick={() => { setSearchQuery(''); setStatusFilter('all'); setReorderMode(true); }} disabled={totalCount < 2} data-testid="reorder-btn">
                <ArrowUpDown className="w-4 h-4" />Reorder
              </button>
              <button type="button" className="qc-btn qc-btn-primary" onClick={openCreate} data-testid={`add-chip-${activeTab}`}>
                <Plus className="w-4 h-4" />Add Chip
              </button>
            </div>
          )}

          <div key={activeTab} className="qc-tab-content" data-testid={`chips-content-${activeTab}`}>
            <ChipTable
              rows={rows}
              Icon={tabDef.icon}
              colors={colors}
              reorderMode={reorderMode}
              searchQuery={searchQuery}
              onReorder={handleReorder}
              onAdd={openCreate}
              onEdit={(chip) => setEditor({ mode: 'edit', chip, category: activeTab })}
              onDuplicate={(chip) => setEditor({ mode: 'duplicate', chip, category: activeTab })}
              onDelete={setChipPendingDelete}
              onToggle={handleToggle}
            />
          </div>
        </section>

        <footer className="qc-footer" data-testid="chips-footer">
          <span className="qc-footer-icon"><Lightbulb className="w-4 h-4" /></span>
          <span>
            <strong>Tip:</strong> These chips appear as quick-select options for processors. Use clear, concise language
            {isMlsCategory(activeTab)
              ? ' — MLS templates are plain text, placeholders are not resolved.'
              : <> and relevant supported placeholders like <code className="qc-token">{'{jurisdiction_name}'}</code>.</>}
          </span>
          {savedAt > 0 && <span className="qc-saved" key={savedAt} data-testid="chips-saved-indicator"><CheckCircle2 className="w-4 h-4" />Changes saved</span>}
        </footer>

        <ChipEditorDialog editor={editor} onSave={handleSave} onClose={() => setEditor(null)} />

        <AlertDialog open={!!chipPendingDelete} onOpenChange={(open) => { if (!open) setChipPendingDelete(null); }}>
          <AlertDialogContent data-testid="delete-chip-dialog" className="max-w-md rounded-2xl">
            <AlertDialogHeader>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <AlertDialogTitle className="text-base font-bold text-slate-900">Delete chip permanently?</AlertDialogTitle>
                  <AlertDialogDescription className="text-sm text-slate-600 mt-1.5 space-y-2">
                    <span className="block">
                      <span className="font-semibold text-slate-900">“{chipPendingDelete?.label}”</span> will be permanently removed from {tabDef.label}. This cannot be undone.
                    </span>
                    <span className="block text-xs text-slate-500">Only want to hide it from new selections? Cancel and set it to <span className="font-semibold">Inactive</span> instead — historical notes keep their text either way.</span>
                  </AlertDialogDescription>
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-4">
              <AlertDialogCancel data-testid="delete-chip-cancel" className="rounded-xl">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-chip-confirm" onClick={confirmDelete} className="rounded-xl bg-red-600 text-white hover:bg-red-700 focus:ring-red-500">Delete permanently</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default AdminQuickChipsSettings;
